import { ajvResolver } from '@hookform/resolvers/ajv'
import ListIcon from '@mui/icons-material/List'
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, Paper, TextField } from '@mui/material'
import { FormAlert, FormHeader } from 'components'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import UserGroupSchema, { IUserGroupFormData } from 'schema/security/user-group'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const UserGroupForm: React.FC = () => {
  const [mainError, setMainError] = useState('')

  const params = useParams<IRouteParams>()
  const firstInputElement = useRef<any>(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = useForm<IUserGroupFormData>({
    resolver: ajvResolver(UserGroupSchema),
    defaultValues: {
      name: '',
      disabled: false,
    },
  })

  // initial load

  useEffect(() => {
    async function loadData() {}

    loadData()
  }, [])

  // main data load

  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data

      await api
        .get(`/user-groups/${id}`)
        .then((response) => {
          const { data } = response.data

          const userGroupResult: IUserGroupFormData = {
            id: data.id,
            name: data.name,
            disabled: data.disabled,
          }

          return userGroupResult
        })
        .then((userGroupResult: IUserGroupFormData) => {
          reset(userGroupResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
    }
  }, [params, params.id])

  // data save

  const onSubmit: SubmitHandler<IUserGroupFormData> = useCallback(async (data) => {
    const payLoad: IUserGroupFormData = {
      id: '',
      name: data.name,
      disabled: data.disabled,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/user-groups`, payLoad)
        .then(history.push('/user-groups'))
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    } else {
      await api
        .post('/user-groups', payLoad)
        .then(history.push('/user-groups/new'))
        .then(() => reset())
        .then(() =>
          setTimeout(() => {
            firstInputElement.current.focus()
          }, 0)
        )
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    }
  }, [])

  const handleChange = (formField: any) => {
    setMainError('')
  }

  return (
    <Paper
      elevation={3}
      className={classes.paper}
    >
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid='form'
      >
        <FormHeader
          title='Grupos de Usuários'
          icon={ListIcon}
          backRoute='/user-groups'
          showSaveButton={true}
          helpText='Nesta opção serão informados os grupos de usuários da aplicação.'
        />

        <FormAlert
          setMainError={setMainError}
          mainError={mainError}
        />

        <Grid
          container
          spacing={1}
          className={mainError === '' ? classes.formContainer : classes.formContainerWithError}
        >
          <Grid
            item
            xs={12}
          >
            <TextField
              id='name'
              label='Nome'
              error={!!errors.name}
              helperText={errors?.name?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              autoFocus
              inputRef={firstInputElement}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 60,
              }}
              {...register('name', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <FormGroup className={classes.checkBox}>
              <Controller
                name='disabled'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                    }
                    label='Inativo'
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

export default UserGroupForm
